import { Discount, LicenseAssignment, Plan, PlanFamily, Subscription } from '@localstack/types';

export const computeNumLicensesUsed = (
  subscription: Subscription,
  licenseAssignments: Array<LicenseAssignment>,
): number | undefined => {
  if (!licenseAssignments) {
    return undefined;
  }

  const numLicensesAssigned = licenseAssignments?.filter(
    (license) => license.subscription_ids?.[0] === subscription.id,
  ).length;

  return numLicensesAssigned;
};

export const formatBillingCadence = (intervalMonths: number): string => {
  if (intervalMonths === 1) return 'monthly';
  if (intervalMonths === 12) return 'yearly';
  return `every ${intervalMonths} months`;
};

const NON_PAYING_FAMILIES = [
  PlanFamily.TRIAL_PLANS,
  PlanFamily.STUDENT,
  PlanFamily.HOBBY_PLANS,
  PlanFamily.FREEMIUM_PLANS,
];

const discountCoversPlan = (discount: Optional<Discount>, plan: Plan): boolean => {
  const restrictedTo = discount?.discount_products;

  return !restrictedTo?.length || plan.products.every((product) => restrictedTo.includes(product.id));
};

type GetEffectiveDiscountParams = {
  promotion?: Optional<Discount>;
  subscription: Optional<Subscription>;
  plan: Plan;
};

type EffectiveDiscount = {
  discount: Optional<Discount>;
  renewalDiscount: Optional<Discount>;
};

export const getEffectiveDiscount = ({
  promotion,
  subscription,
  plan,
}: GetEffectiveDiscountParams): EffectiveDiscount => {
  if (promotion) return { discount: promotion, renewalDiscount: promotion };

  const isNonPayingCurrentPlan = subscription?.plan.family && NON_PAYING_FAMILIES.includes(subscription?.plan.family);

  if (!subscription || isNonPayingCurrentPlan) return { discount: plan.discount, renewalDiscount: plan.discount };

  const isSwitchingPlan = subscription.plan.id !== plan.id;
  const discountEndsOnPlanChange =
    !!subscription.discount?.clear_on_cancel || !discountCoversPlan(subscription.discount, plan);
  if (isSwitchingPlan && discountEndsOnPlanChange) return { discount: undefined, renewalDiscount: undefined };

  const reachesRenewal = subscription.discount_reaches_renewal !== false;

  return { discount: subscription.discount, renewalDiscount: reachesRenewal ? subscription.discount : undefined };
};

/**
 * Groups plans that are variants of each other (e.g. monthly vs yearly of the same plan).
 * Plans sharing a `multi_interval_plan_family` are grouped together; others stand alone.
 *
 * Example output: [[aws_base_yearly, aws_base_monthly], [aws_ultimate_yearly, aws_ultimate_monthly], [freemium]]
 */
export const groupMultiIntervalPlans = (plans: Plan[]): Plan[][] =>
  Array.from(
    plans
      .reduce((map, plan) => {
        const key = plan.multi_interval_plan_family ? `${plan.family}-${plan.emulator}` : plan.id;
        const group = map.get(key);
        if (group) group.push(plan);
        else map.set(key, [plan]);
        return map;
      }, new Map<string, Plan[]>())
      .values(),
  );

export const roundMonthsToWholeIntervals = (durationMonths: number, intervalMonths: number): number => {
  const interval = Math.max(intervalMonths, 1);

  return Math.ceil(durationMonths / interval) * interval;
};
